import React, { Component } from 'react';
import { isFontReady, waitForFont } from '@ifit/mp-font-watcher';
import { clampText } from './clamp-text';
import classnames from 'classnames';

interface IProps {
  text: string;
  basedOn?: 'words' | 'letters';
  children?: string;
  className?: string;
  ellipsis?: string;
  maxChar?: number;
  maxLines?: number;
  minWidth?: number;
  recalculateOnResize?: boolean;
}

interface IState {
  containerWidth: number;
  font: string;
  fontReady: boolean;
  waitingForFont: { stop: () => void } | null;
}

class LineWrap extends Component<IProps, IState> {
  containerRef = React.createRef<HTMLDivElement>();
  state: IState = {
    containerWidth: 0,
    font: '',
    fontReady: false,
    waitingForFont: null,
  };

  updateDimensions = () => {
    const element = this.containerRef.current;
    if (element) {
      const containerWidth = element?.getBoundingClientRect().width
      const styles = window.getComputedStyle(element);
      const fontReady = isFontReady(styles.fontFamily);
      this.setState({
        containerWidth: containerWidth,
        font: [
          styles.fontStyle,
          styles.fontVariant,
          styles.fontWeight,
          styles.fontSize,
          styles.fontFamily,
        ].join(' '),
        fontReady,
      });
      if (!fontReady && !this.state.waitingForFont) {
        this.setState({
          waitingForFont: waitForFont(styles.fontFamily, this.updateDimensions),
        });
      }
    }
  };

  componentDidMount() {
    this.updateDimensions();
    if (this.props.recalculateOnResize) {
      window.addEventListener('resize', this.updateDimensions);
    }
  }

  componentWillUnmount() {
    if (this.props.recalculateOnResize) {
      window.removeEventListener('resize', this.updateDimensions);
    }
    if (this.state.waitingForFont) {
      this.state.waitingForFont.stop();
    }
  }

  render() {
    const { font, fontReady, containerWidth } = this.state;
    const {
      basedOn = 'words',
      children,
      className,
      ellipsis = '...',
      maxChar,
      maxLines = 2,
      minWidth,
    } = this.props;
    let text = this.props.text || children || '';
    const style = minWidth ? { minWidth: `${minWidth}px` } : {};

    if (text && containerWidth && fontReady) {
      text = clampText({
        text,
        font,
        basedOn,
        ellipsis,
        maxLines,
        maxChar,
        width: containerWidth,
      });
    }

    if (maxChar && text.length > maxChar) {
      text = text.substring(0, maxChar).trim() + ellipsis;
    }

    return (
      <div
        ref={this.containerRef}
        className={classnames(className)}
        style={style}
      >
        {text}
      </div>
    );
  }
}

export default LineWrap;
